// [Phase 6a foundation] Typed fetch wrapper backing TanStack Query
// hooks. Every request:
//   - Resolves a fresh JWT through useAuth().getFreshJwt() — auto-
//     refreshes if within REFRESH_LEAD_MS of expiry.
//   - Throws ApiError on non-2xx so TanStack Query routes to the
//     `error` branch with a typed shape.
//   - Sets `accept: application/json` and `content-type: application/json`
//     defaults; callers can override via init.headers.
//
// Base URL comes from VITE_ADMIN_API_BASE / VITE_MCP_BASE / etc., with
// localhost defaults for dev. The hook indirection (useApi() returns
// the bound fetch fn) keeps tests trivial — pass a fake.
//
// Local-dev IAP shim: the admin server (`csai-admin`) authenticates
// callers via two headers that Identity-Aware Proxy normally injects:
//
//   x-goog-authenticated-user-id: <uid>
//   x-csai-iap-claims:            JSON of { role, studioId, accessTier }
//
// In production the web app is behind IAP and never sets these — IAP
// signs and forwards them. In local dev there is no IAP, so the web
// app emits them itself from the in-memory session. This is gated on
// `import.meta.env.DEV` so production bundles never carry the shim and
// can never spoof IAP claims.
import { useCallback } from "react";
import { useAuth, useSession } from "../auth/AuthProvider";
import type { WebSession } from "../auth/types";
import { isSessionExpiredError } from "../auth/sessionExpired";

// S-411 — the tenant the user is currently viewing. The backend resolves the
// caller's per-tenant role from their membership in this tenant.
export const ACTIVE_TENANT_HEADER = "x-csai-active-tenant";

// Attaches the active-tenant header (prod + dev) and, in dev only, the IAP shim
// headers the local admin trusts in place of a real IAP/Bearer assertion. Email
// rides in jwtClaims so the dev active-tenant middleware can resolve membership
// (prod reads it from the verified Firebase token).
function applyTenantAndDevShim(
  headers: Headers,
  session: WebSession | null
): void {
  // Don't clobber a caller-pinned active-tenant header (the chat hooks pin it to
  // the tenant they're scoped to, so a chat request always carries the right
  // tenant even before the derived session.activeTenantId has settled).
  const activeTenant = session?.activeTenantId;
  if (
    !headers.has(ACTIVE_TENANT_HEADER) &&
    typeof activeTenant === "string" &&
    activeTenant.length > 0
  ) {
    headers.set(ACTIVE_TENANT_HEADER, activeTenant);
  }
  if (import.meta.env.DEV && session !== null) {
    headers.set("x-goog-authenticated-user-id", session.uid);
    headers.set(
      "x-csai-iap-claims",
      JSON.stringify({
        role: session.role,
        studioId: session.studioId,
        accessTier: session.accessTier,
        jwtClaims: { email: session.email },
      })
    );
  }
}

export type ApiErrorBody = {
  code?: string;
  message?: string;
};

export class ApiError extends Error {
  override readonly name = "ApiError";
  constructor(
    readonly status: number,
    readonly url: string,
    readonly body: unknown
  ) {
    super(
      isUnreachableShape(status, body)
        ? `${UNREACHABLE_MESSAGE} (${url})`
        : `API ${status} ${url}`
    );
  }

  /** True when nothing on the other end answered — see isUnreachableShape. */
  get unreachable(): boolean {
    return isUnreachableShape(this.status, this.body);
  }
}

// [dev-loop bugfix] "API 500" was the copy for a backend that never answered.
// The dev stack's admin process restarts whenever packages/*/dist changes, and
// a request that lands mid-restart is refused at the socket: Vite's proxy turns
// ECONNREFUSED into a bare 500 with no JSON body, so the UI reported a server
// error for a server that wasn't running. Observed live as
// "Couldn't attach <file>: API 500 /api/admin/chat/sessions/.../attachments"
// with no matching [5xx] line in admin.log, because the request never got there.
//
// The discriminator is the BODY, not the status: every real 5xx from our
// services carries a JSON `{ code, message }` (admin's errorMapper, chatbot's
// error middleware). A 5xx WITHOUT that shape did not come from application
// code — it's the proxy/gateway reporting it couldn't reach one. Status 0 is
// our own marker for a fetch that never produced a response at all.
const UNREACHABLE_MESSAGE = "Backend unreachable — the API didn't respond";

function isUnreachableShape(status: number, body: unknown): boolean {
  if (status === 0) return true;
  if (status < 500) return false;
  if (body === null || typeof body !== "object") return true;
  return typeof (body as { code?: unknown }).code !== "string";
}

/** Dev-only hint appended to the unreachable copy — the fix is local, and this
 *  is the one environment where the user can act on it. */
function unreachableCopy(): string {
  return import.meta.env.DEV
    ? `${UNREACHABLE_MESSAGE}. Is the dev stack running? (scripts/dev-up.sh — the admin also restarts briefly after a packages/* rebuild.)`
    : `${UNREACHABLE_MESSAGE}. Try again in a moment.`;
}

/**
 * Build an ApiError from a non-2xx (or otherwise failed) response: parse the
 * body as JSON, falling back to text, then null. Returned (not thrown) so call
 * sites keep `throw await apiError(...)` and TypeScript's control-flow narrowing.
 */
async function apiError(res: Response, url: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => null);
  }
  return new ApiError(res.status, url, body);
}

/**
 * fetch() itself rejecting (dev server down, DNS/TLS failure, offline) is the
 * same user-facing situation as a proxy that can't reach the backend, so it
 * becomes an ApiError with status 0 rather than a raw TypeError that every call
 * site has to recognize on its own. AbortError is re-thrown untouched — a
 * caller-cancelled request is not a failure.
 */
async function fetchOrUnreachable(
  url: string,
  init: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, url, null);
  }
}

/** True when the request never reached (or never got an answer from) the API —
 *  a refused/failed connection or a proxy 5xx, as opposed to a real backend
 *  error. Exported so a screen can offer "retry" instead of "report a bug".
 *
 *  [live-verified] Deliberately NOT a bare `instanceof` check. Vite's HMR can
 *  leave two instances of this module in one page (a `?t=` variant alongside
 *  the clean URL), and an error built by one instance fails `instanceof`
 *  against the other's class — silently, as `false`. Caught in the dev loop:
 *  isBackendUnreachableError() said true while the retry policy that closed
 *  over a different module instance said false for the SAME error. That would
 *  have disabled the retry in exactly the environment it was built for. The
 *  duck-typed branch keys on `name`, which the class pins as a readonly field. */
export function isBackendUnreachableError(error: unknown): boolean {
  if (error instanceof ApiError) return error.unreachable;
  if (error === null || typeof error !== "object") return false;
  const e = error as { name?: unknown; status?: unknown; body?: unknown };
  if (e.name !== "ApiError" || typeof e.status !== "number") return false;
  return isUnreachableShape(e.status, e.body);
}

/** Set the bearer token (when present) + the active-tenant / dev-IAP-shim headers. */
function applyAuth(
  headers: Headers,
  jwt: string | null,
  session: WebSession | null
): void {
  if (jwt !== null) {
    headers.set("authorization", `Bearer ${jwt}`);
  }
  applyTenantAndDevShim(headers, session);
}

/**
 * Pulls a user-facing message out of an unknown error caught from a
 * TanStack-Query mutation/query. Returns the backend's `body.message`
 * when the error is an `ApiError` whose body matches `ApiErrorBody`;
 * otherwise null so the caller falls back to its own generic copy.
 */
export function getApiErrorMessage(error: unknown): string | null {
  // [S-637 slice 2] A lapsed session throws a typed SessionExpiredError from the
  // auth layer before the request even fires. Map it to coherent copy so any
  // screen surfacing this helper says the same thing the re-auth modal does —
  // never the old null → generic "something broke" state (Mode 2).
  if (isSessionExpiredError(error)) {
    return "Your session expired. Sign back in to continue.";
  }
  if (!(error instanceof ApiError)) return null;
  // Nothing answered — there is no backend `message` to surface, and "API 500"
  // sends you hunting through server logs for a request that never arrived.
  if (error.unreachable) return unreachableCopy();
  const body = error.body;
  if (body === null || typeof body !== "object") return null;
  const message = (body as { message?: unknown }).message;
  return typeof message === "string" && message.length > 0 ? message : null;
}

export type ApiInit = Omit<RequestInit, "body"> & {
  body?: unknown;
};

export type ApiClient = <T>(path: string, init?: ApiInit) => Promise<T>;

export type ApiBaseUrls = {
  admin: string;
  mcp: string;
  layer2: string;
};

const DEFAULT_BASES: ApiBaseUrls = {
  // Defaults are the same-origin Vite-proxy paths (see vite.config.ts).
  // VITE_*_BASE overrides apply when pointing at a deployed backend.
  admin: import.meta.env.VITE_ADMIN_API_BASE ?? "/api/admin",
  mcp: import.meta.env.VITE_MCP_BASE ?? "/api/mcp",
  layer2: import.meta.env.VITE_LAYER2_BASE ?? "/api/layer2",
};

/**
 * Returns an ApiClient bound to a base URL key. Re-binds on auth state
 * change so cached query results invalidate automatically when the
 * caller logs out.
 */
/**
 * Sibling of useApi for binary downloads. Returns the raw Blob and the
 * suggested filename (parsed from Content-Disposition: attachment;
 * filename="..."). Used by template DOCX export (S-213) — JSON parsing
 * would corrupt the response.
 */
export function useApiBlob(
  target: keyof ApiBaseUrls = "admin"
): (path: string, init?: ApiInit) => Promise<{ blob: Blob; filename: string | null }> {
  const { getFreshJwt } = useAuth();
  const session = useSession();
  const baseUrl = DEFAULT_BASES[target];

  return useCallback(
    async (path: string, init: ApiInit = {}) => {
      const jwt = await getFreshJwt();
      const headers = new Headers(init.headers);
      if (
        init.body !== undefined &&
        typeof init.body !== "string" &&
        !headers.has("content-type")
      ) {
        headers.set("content-type", "application/json");
      }
      applyAuth(headers, jwt, session);

      const url = `${baseUrl}${path}`;
      const res = await fetchOrUnreachable(url, {
        ...init,
        headers,
        body:
          init.body === undefined
            ? undefined
            : typeof init.body === "string"
              ? init.body
              : JSON.stringify(init.body),
      });
      if (!res.ok) throw await apiError(res, url);
      const disposition = res.headers.get("content-disposition") ?? "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match !== null ? match[1] ?? null : null;
      const blob = await res.blob();
      return { blob, filename };
    },
    [baseUrl, getFreshJwt, session]
  );
}

export type ApiStreamInit = {
  method?: string;
  body?: BodyInit;
  headers?: HeadersInit;
  // [chat-stop] Abort the in-flight streaming request (the Stop button). fetch
  // rejects with AbortError, which fetchOrUnreachable re-throws untouched so the
  // caller can treat a user cancel as a clean stop, not a failure.
  signal?: AbortSignal;
};

/**
 * Sibling of useApi for newline-delimited JSON (NDJSON) streaming responses
 * (S-372 review stream). Same auth (JWT + dev IAP shim) and base URL, but
 * reads the body incrementally and invokes `onLine` for each complete line.
 * Lets the FormData body through untouched (the browser sets the multipart
 * boundary). Resolves when the stream ends.
 */
export function useApiStream(
  target: keyof ApiBaseUrls = "admin"
): (path: string, init: ApiStreamInit, onLine: (line: string) => void) => Promise<void> {
  const { getFreshJwt } = useAuth();
  const session = useSession();
  const baseUrl = DEFAULT_BASES[target];

  return useCallback(
    async (path, init, onLine) => {
      const jwt = await getFreshJwt();
      const headers = new Headers(init.headers);
      applyAuth(headers, jwt, session);
      const url = `${baseUrl}${path}`;
      const res = await fetchOrUnreachable(url, {
        method: init.method ?? "POST",
        headers,
        body: init.body,
        ...(init.signal !== undefined ? { signal: init.signal } : {}),
      });
      if (!res.ok || res.body === null) throw await apiError(res, url);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf("\n")) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (line.length > 0) onLine(line);
        }
      }
      const tail = buf.trim();
      if (tail.length > 0) onLine(tail);
    },
    [baseUrl, getFreshJwt, session]
  );
}

export function useApi(target: keyof ApiBaseUrls = "admin"): ApiClient {
  const { getFreshJwt } = useAuth();
  const session = useSession();
  const baseUrl = DEFAULT_BASES[target];

  return useCallback(
    async <T,>(path: string, init: ApiInit = {}): Promise<T> => {
      const jwt = await getFreshJwt();
      const headers = new Headers(init.headers);
      if (!headers.has("accept")) headers.set("accept", "application/json");
      // FormData carries its own multipart boundary; the browser must set
      // the content-type header, so we never override it here. Plain JSON
      // bodies still default to application/json.
      const isFormData =
        typeof FormData !== "undefined" && init.body instanceof FormData;
      if (
        init.body !== undefined &&
        !isFormData &&
        !headers.has("content-type")
      ) {
        headers.set("content-type", "application/json");
      }
      // Auth (bearer) + dev-only IAP shim — see file header.
      applyAuth(headers, jwt, session);

      const url = `${baseUrl}${path}`;
      const res = await fetchOrUnreachable(url, {
        ...init,
        headers,
        body:
          init.body === undefined
            ? undefined
            : typeof init.body === "string" || isFormData
              ? (init.body as BodyInit)
              : JSON.stringify(init.body),
      });
      if (!res.ok) throw await apiError(res, url);
      // 204 No Content
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },
    [baseUrl, getFreshJwt]
  );
}
